<template>
    <div>
        <!-- ===== TOP ROW: General info + Insured + Holder ===== -->
        <div class="row g-3 mb-3">

            <!-- General Info -->
            <div class="col-lg-4">
                <div class="meta-section-box h-100">
                    <div class="meta-section-header">
                        <span class="meta-header-icon meta-header-icon--blue">
                            <i class="bi bi-file-earmark-text-fill"></i>
                        </span>
                        <h6 class="mb-0 fw-bold meta-header-title">Ümumi məlumat</h6>
                    </div>
                    <div class="meta-content-body">
                        <div class="meta-row">
                            <span class="meta-label">Təklif nömrəsi</span>
                            <span class="meta-value">
                                <span class="doc-number-badge">{{ data.quotaFullNumber }}</span>
                            </span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Təklif tarixi</span>
                            <span class="meta-value font-monospace">{{ data.quotaDateStr }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Baş Müqavilə №</span>
                            <span class="meta-value">
                                <span class="doc-number-badge doc-number-badge--purple">{{ data.generalFullNumber }}</span>
                            </span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Baş Müqavilə tarixi</span>
                            <span class="meta-value font-monospace">{{ data.generalDate }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Müddət</span>
                            <span class="meta-value">
                                <span class="meta-chip meta-chip--blue">{{ data.period }} ay</span>
                            </span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Valyuta</span>
                            <span class="meta-value">
                                <span class="meta-chip meta-chip--green">{{ data.currency }}</span>
                            </span>
                        </div>
                        <div class="meta-row border-bottom-0">
                            <span class="meta-label">Ödəniş qaydası</span>
                            <span class="meta-value">
                                <span class="meta-chip meta-chip--amber">{{ data.payType }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Insured Company -->
            <div class="col-lg-4">
                <div class="meta-section-box h-100">
                    <div class="meta-section-header">
                        <span class="meta-header-icon meta-header-icon--purple">
                            <i class="bi bi-buildings-fill"></i>
                        </span>
                        <h6 class="mb-0 fw-bold meta-header-title">Sığortalı</h6>
                    </div>
                    <div class="meta-content-body">
                        <div class="meta-row">
                            <span class="meta-label">Şirkət adı</span>
                            <span class="meta-value fw-bold">{{ data.insured?.name }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">VÖEN</span>
                            <span class="meta-value font-monospace text-info fw-bold">{{ data.insured?.voen }}</span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Telefon</span>
                            <span class="meta-value">
                                <a :href="'tel:' + data.insured?.phone" class="phone-meta-link">
                                    <i class="bi bi-telephone me-1"></i>{{ data.insured?.phone }}
                                </a>
                            </span>
                        </div>
                        <div class="meta-row">
                            <span class="meta-label">Sektor</span>
                            <span class="meta-value">
                                <span class="meta-chip meta-chip--blue">{{ data.insured?.clientSector }}</span>
                            </span>
                        </div>
                        <div class="meta-row border-bottom-0">
                            <span class="meta-label">Ünvan</span>
                            <span class="meta-value text-muted" style="text-align: right; line-height:1.4;">{{ data.insured?.address }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Holder + Responsible Persons -->
            <div class="col-lg-4">
                <!-- Ödəyici -->
                <div class="meta-section-box mb-3">
                    <div class="meta-section-header">
                        <span class="meta-header-icon meta-header-icon--green">
                            <i class="bi bi-person-fill-check"></i>
                        </span>
                        <h6 class="mb-0 fw-bold meta-header-title">Ödəyici</h6>
                    </div>
                    <div class="meta-content-body">
                        <div class="meta-row">
                            <span class="meta-label">Tam adı</span>
                            <span class="meta-value fw-bold">{{ data.beneficiary?.name }}</span>
                        </div>
                        <div class="meta-row border-bottom-0">
                            <span class="meta-label">Ünvan</span>
                            <span class="meta-value text-muted" style="text-align: right; line-height:1.4;">{{ data.beneficiary?.address }}</span>
                        </div>
                    </div>
                </div>

                <!-- Məsul şəxslər -->
                <div class="meta-section-box">
                    <div class="meta-section-header">
                        <span class="meta-header-icon meta-header-icon--amber">
                            <i class="bi bi-people-fill"></i>
                        </span>
                        <h6 class="mb-0 fw-bold meta-header-title">Məsul şəxslər</h6>
                    </div>
                    <div class="meta-content-body">
                        <div class="meta-row">
                            <span class="meta-label">Agent</span>
                            <span class="meta-value d-flex align-items-center gap-2">
                                <span class="person-avatar">{{ initials(data.agentName) }}</span>
                                {{ data.agentName }}
                            </span>
                        </div>
                        <div class="meta-row border-bottom-0">
                            <span class="meta-label">Kurator</span>
                            <span class="meta-value d-flex align-items-center gap-2">
                                <span class="person-avatar person-avatar--purple">{{ initials(data.curatorName) }}</span>
                                {{ data.curatorName }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ===== STATUS TIMELINE ===== -->
        <div class="uw-status-section">
            <div class="uw-status-header">
                <span class="meta-header-icon meta-header-icon--blue" style="width:24px;height:24px;font-size:0.8rem;">
                    <i class="bi bi-activity"></i>
                </span>
                <span class="fw-bold" style="font-size: 0.87rem;">Status tarixi</span>
            </div>
            <div class="uw-timeline">
                <div
                    v-for="(item, idx) in statusData"
                    :key="idx"
                    class="uw-timeline-item"
                    :class="{ 'uw-timeline-item--last': idx === statusData.length - 1 }"
                >
                    <div class="uw-tl-dot" :class="getDotClass(item.statusId)">
                        <i :class="getDotIcon(item.statusId)"></i>
                    </div>
                    <div class="uw-tl-line" v-if="idx < statusData.length - 1"></div>
                    <div class="uw-tl-content">
                        <span class="uw-status-badge" :class="getBadgeClass(item.statusId)">
                            {{ item.status }}
                        </span>
                        <div class="uw-tl-meta">
                            <span class="uw-tl-user">
                                <i class="bi bi-person me-1"></i>{{ item.user }}
                            </span>
                            <span class="uw-tl-time">
                                <i class="bi bi-clock me-1"></i>{{ item.time }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "UnderwritingInfoData",
    props: {
        data: Object,
        statusData: Array
    },
    methods: {
        initials(name) {
            if (!name) return "?";
            return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
        },
        getDotClass(statusId) {
            if (statusId === 2) return "dot--approved";
            if (statusId === 1) return "dot--rejected";
            if (statusId === 4) return "dot--pending";
            return "dot--new";
        },
        getDotIcon(statusId) {
            if (statusId === 2) return "bi bi-check-lg";
            if (statusId === 1) return "bi bi-x-lg";
            if (statusId === 4) return "bi bi-arrow-right";
            return "bi bi-plus";
        },
        getBadgeClass(statusId) {
            if (statusId === 2) return "badge--approved";
            if (statusId === 1) return "badge--rejected";
            if (statusId === 4) return "badge--in-progress";
            return "badge--new";
        }
    }
}
</script>

<style scoped lang="scss">
// ---- Section Box ----
.meta-section-box {
    background: var(--app-surface-soft, #f8fafc);
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: var(--app-radius, 8px);
    overflow: hidden;
}

.meta-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--app-surface, #ffffff);
    border-bottom: 1px solid var(--app-border, #e2e8f0);
}

.meta-header-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.88rem;
    flex-shrink: 0;

    &--blue { background: rgba(11, 79, 156, 0.12); color: var(--bs-info, #0b4f9c); }
    &--purple { background: rgba(147, 51, 234, 0.12); color: #9333ea; }
    &--green { background: rgba(16, 185, 129, 0.12); color: #059669; }
    &--amber { background: rgba(245, 158, 11, 0.12); color: #d97706; }
}

.meta-header-title {
    font-size: 0.88rem;
    color: var(--app-heading, #1e293b);
}

.meta-content-body {
    padding: 4px 14px;
}

.meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--app-border, #e2e8f0);
    font-size: 0.82rem;
}

.meta-label {
    color: var(--app-text, #64748b);
    font-weight: 550;
    min-width: 130px;
    flex-shrink: 0;
}

.meta-value {
    color: var(--app-heading, #1e293b);
    text-align: right;
    word-break: break-word;
}

// ---- Chips & Badges ----
.meta-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 0.74rem;
    font-weight: 700;

    &--blue { background: rgba(11, 79, 156, 0.1); color: var(--bs-info, #0b4f9c); }
    &--green { background: rgba(16, 185, 129, 0.12); color: #059669; }
    &--amber { background: rgba(245, 158, 11, 0.14); color: #b45309; }
}

.doc-number-badge {
    font-family: monospace;
    font-size: 0.76rem;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 5px;
    background: rgba(11, 79, 156, 0.1);
    color: var(--bs-info, #0b4f9c);
    border: 1px solid rgba(11, 79, 156, 0.18);
    letter-spacing: 0.04em;

    &--purple {
        background: rgba(147, 51, 234, 0.1);
        color: #9333ea;
        border-color: rgba(147, 51, 234, 0.2);
    }
}

.phone-meta-link {
    color: var(--bs-info, #0b4f9c);
    text-decoration: none;
    font-weight: 600;
    &:hover { text-decoration: underline; }
}

.person-avatar {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(11, 79, 156, 0.15);
    color: var(--bs-info, #0b4f9c);
    font-size: 0.62rem;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--purple {
        background: rgba(147, 51, 234, 0.15);
        color: #9333ea;
    }
}

// ---- Status Timeline ----
.uw-status-section {
    background: var(--app-surface, #ffffff);
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: var(--app-radius, 8px);
    overflow: hidden;
}

.uw-status-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: var(--app-surface-soft, #f8fafc);
    border-bottom: 1px solid var(--app-border, #e2e8f0);
    color: var(--app-heading, #1e293b);
}

.uw-timeline {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 0;
}

.uw-timeline-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    position: relative;
}

.uw-tl-dot {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 800;
    flex-shrink: 0;
    z-index: 1;
    position: relative;

    &.dot--approved { background: rgba(16, 185, 129, 0.18); color: #059669; border: 2px solid rgba(16, 185, 129, 0.45); }
    &.dot--rejected { background: rgba(239, 68, 68, 0.15); color: #dc2626; border: 2px solid rgba(239, 68, 68, 0.4); }
    &.dot--pending { background: rgba(245, 158, 11, 0.15); color: #d97706; border: 2px solid rgba(245, 158, 11, 0.4); }
    &.dot--new { background: rgba(11, 79, 156, 0.12); color: var(--bs-info, #0b4f9c); border: 2px solid rgba(11, 79, 156, 0.3); }
}

.uw-tl-line {
    position: absolute;
    left: 13px;
    top: 28px;
    width: 2px;
    height: calc(100% + 10px);
    background: var(--app-border, #e2e8f0);
    z-index: 0;
}

.uw-tl-content {
    flex: 1;
    padding-bottom: 20px;
    padding-top: 2px;
}

.uw-timeline-item--last .uw-tl-content {
    padding-bottom: 0;
}

.uw-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 0.74rem;
    font-weight: 700;
    margin-bottom: 6px;

    &.badge--approved { background: rgba(16, 185, 129, 0.15); color: #059669; border: 1px solid rgba(16, 185, 129, 0.3); }
    &.badge--rejected { background: rgba(239, 68, 68, 0.12); color: #dc2626; border: 1px solid rgba(239, 68, 68, 0.3); }
    &.badge--in-progress { background: rgba(245, 158, 11, 0.12); color: #b45309; border: 1px solid rgba(245, 158, 11, 0.3); }
    &.badge--new { background: rgba(11, 79, 156, 0.1); color: var(--bs-info, #0b4f9c); border: 1px solid rgba(11, 79, 156, 0.2); }
}

.uw-tl-meta {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 0.78rem;
    color: var(--app-text, #64748b);
    flex-wrap: wrap;
}

.uw-tl-user, .uw-tl-time {
    display: inline-flex;
    align-items: center;
}

// ---- Dark Mode ----
[data-theme="dark"] {
    .meta-section-box { background: #141b22; border-color: #283544; }
    .meta-section-header { background: #182029; border-color: #283544; }
    .meta-header-title { color: #f1f5f9; }
    .meta-row { border-color: #242f3d; }
    .meta-label { color: #94a3b8; }
    .meta-value { color: #f1f5f9; }
    .doc-number-badge { background: rgba(59, 130, 246, 0.2); color: #93c5fd; border-color: rgba(59, 130, 246, 0.35); }
    .phone-meta-link { color: #93c5fd; }

    .uw-status-section { background: #182029; border-color: #283544; }
    .uw-status-header { background: #141b22; border-color: #283544; color: #f1f5f9; }
    .uw-tl-line { background: #283544; }
    .uw-tl-meta { color: #94a3b8; }
}
</style>